import { CheckCircle2, CircleHelp, Info, Loader } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useForceMetadataUpdateMutation,
  useForceMintMutation,
} from "@/data/mutations";
import { useGasPriceQuery } from "@/data/queries";
import { SKU } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import {
  ENVIRONMENT_NETWORKS,
  GasInfoByNetwork,
  generateAddressLink,
  Network,
  NetworkNameByChainId,
} from "@/lib/chains";
import { SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

import { GasPriceAlert } from "./gas-price-alert";

type Props = {
  sku: SKU;
};

export const BlockchainTab: FC<Props> = ({ sku }) => {
  const form = useFormContext<SKUFormValues>();

  const forceUpdateMutation = useForceMetadataUpdateMutation();
  const forceMintMutation = useForceMintMutation();
  const { openDialog } = useDialogState();
  const { session } = useStore();
  const selectedChainId = form.watch("nft_chain_id");
  const { data } = useGasPriceQuery(
    selectedChainId ? (parseInt(selectedChainId) as Network) : undefined,
  );

  const lacksPermission = useMemo(() => {
    return !canEditSKU(session?.permissions);
  }, [session]);

  const onSubmit = useCallback(
    (values: SKUFormValues) => {
      const tokenName = sku.name.replace(/\s/gi, "").slice(0, 25);
      const tokenSymbol = sku.name.replace(/\s/gi, "").slice(0, 15);

      openDialog({
        name: "confirm-blockchain",
        params: {
          skuId: sku.id,
          tokenName,
          tokenSymbol,
          chainId: Number(values.nft_chain_id) as Network,
          currentGas: data?.currentGas,
          gasLow: data?.gasLow,
        },
      });
    },
    [sku, data, openDialog],
  );

  const handleForceUpdate = () => {
    forceUpdateMutation.mutate(sku.id);
  };
  const handleForceMint = () => {
    forceMintMutation.mutate(sku.id);
  };

  return (
    <div>
      <Alert className="mt-6">
        <Info className="h-4 w-4" />
        <AlertTitle className="text-base">Why is there blockchain?</AlertTitle>
        <AlertDescription className="text-sm">
          Legitimate gives you the option of attaching your SKU and its Digital
          Products to a blockchain for full security and anti-fraud prevention.
          This action is recommended to ensure authenticity and accurate history
          of ownership. View our documentation to learn more about the benefits
          of connecting your products to the blockchain.
        </AlertDescription>
      </Alert>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Enable Blockchain
      </h3>

      {sku.nft_contract_address && (
        <Alert className="mt-6">
          <CheckCircle2 className="h-4 w-4 stroke-green-600" />
          <AlertTitle className="text-base text-green-600">
            Blockchain Successfully Enabled
          </AlertTitle>
          <AlertDescription className="text-sm">
            <Link
              to={generateAddressLink(
                sku.nft_chain_id as Network,
                sku.nft_contract_address,
              )}
              target="_blank"
              className="hover:underline"
            >
              Your Digital Product for SKU {sku.id} was deployed to{" "}
              {NetworkNameByChainId[sku.nft_chain_id as Network]}. This cannot
              be changed or reversed.
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <form className="mt-6" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="nft_chain_id"
          disabled={!!sku.nft_contract_address || lacksPermission}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Blockchain</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={field.disabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a blockchain" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ENVIRONMENT_NETWORKS.map((blockchain) => (
                    <SelectItem
                      key={blockchain}
                      value={blockchain.toString()}
                      disabled={blockchain === Network.Ethereum}
                    >
                      {NetworkNameByChainId[blockchain]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                This is the blockchain for the Digital Product Passport and
                cannot be changed after the contract is deployed.
              </FormDescription>
            </FormItem>
          )}
        />

        {selectedChainId && data && !sku.nft_contract_address && (
          <GasPriceAlert
            chainId={parseInt(selectedChainId) as keyof typeof GasInfoByNetwork}
            {...data}
          />
        )}

        {sku.nft_contract_address && (
          <Accordion type="single" collapsible className="mt-8 border-b">
            <AccordionItem value="blockchain-advanced">
              <AccordionTrigger>Advanced</AccordionTrigger>
              <AccordionContent>
                <Card className="p-6 space-y-6">
                  <div className="flex space-x-2">
                    <CircleHelp className="w-5 h-5 text-muted-foreground" />
                    <p className="text-muted-foreground font-medium text-sm">
                      Updates to name, image, and description are queued to be
                      written to the blockchain. In the event that Digital
                      Products are not being updated or successfully associated
                      with a SKU, you may manually force updates below.
                    </p>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">Force Metadata Update</p>
                      <p className="font-medium text-sm text-muted-foreground">
                        Update name, image and description of Digital Products
                      </p>
                    </div>
                    <Button
                      onClick={handleForceUpdate}
                      disabled={
                        forceUpdateMutation.isPending || lacksPermission
                      }
                      className="w-20"
                      type="button"
                    >
                      {forceUpdateMutation.isPending ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : null}
                      Update
                    </Button>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">Force Create</p>
                      <p className="font-medium text-sm text-muted-foreground">
                        Create any remaining Digital Products that have not yet
                        been associated with the SKU
                      </p>
                    </div>
                    <Button
                      onClick={handleForceMint}
                      disabled={forceMintMutation.isPending || lacksPermission}
                      className="w-20"
                      type="button"
                    >
                      {forceMintMutation.isPending ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : null}
                      Create
                    </Button>
                  </div>
                </Card>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <Button
          className="mt-6"
          type="submit"
          disabled={!!sku.nft_contract_address || lacksPermission}
        >
          Enable
        </Button>
      </form>
    </div>
  );
};
